import useSessionStorage from '@rio-cloud/rio-uikit/useSessionStorage';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const missingPocketDriverDataSourceBannerStorageKey = 'missing-pd-data-source-banner-dismissed';

type MissingPocketDriverDataSourceBannerState = {
    isDismissed: boolean;
    missingCount: number;
    dismiss: () => void;
    restore: () => void;
    setMissingCount: (count: number) => void;
};

const MissingPocketDriverDataSourceBannerStateContext = createContext<
    MissingPocketDriverDataSourceBannerState | undefined
>(undefined);

type MissingPocketDriverDataSourceBannerStateProviderProps = {
    children: ReactNode;
};

export const MissingPocketDriverDataSourceBannerStateProvider = ({
    children,
}: MissingPocketDriverDataSourceBannerStateProviderProps) => {
    const [isDismissed, setIsDismissed] = useSessionStorage<boolean>(
        missingPocketDriverDataSourceBannerStorageKey,
        false
    );
    const [missingCount, setMissingCount] = useState(0);

    const dismiss = useCallback(() => {
        setIsDismissed(true);
    }, [setIsDismissed]);

    const restore = useCallback(() => {
        setIsDismissed(false);
    }, [setIsDismissed]);

    const value = useMemo(
        () => ({
            dismiss,
            isDismissed: Boolean(isDismissed),
            missingCount,
            restore,
            setMissingCount,
        }),
        [dismiss, isDismissed, missingCount, restore]
    );

    return (
        <MissingPocketDriverDataSourceBannerStateContext.Provider value={value}>
            {children}
        </MissingPocketDriverDataSourceBannerStateContext.Provider>
    );
};

type MissingPocketDriverDataSourceBannerStateSyncProps = {
    missingCount: number;
};

export const MissingPocketDriverDataSourceBannerStateSync = ({
    missingCount,
}: MissingPocketDriverDataSourceBannerStateSyncProps) => {
    const { setMissingCount } = useMissingPocketDriverDataSourceBannerState();

    useEffect(() => {
        setMissingCount(missingCount);
    }, [missingCount, setMissingCount]);

    useEffect(
        () => () => {
            setMissingCount(0);
        },
        [setMissingCount]
    );

    return null;
};

export const useMissingPocketDriverDataSourceBannerState = () => {
    const context = useContext(MissingPocketDriverDataSourceBannerStateContext);

    if (!context) {
        throw new Error('MissingPocketDriverDataSourceBannerStateProvider is required.');
    }

    return context;
};
